import React, { useState, useEffect } from 'react';
import { Box, InputBase, IconButton, useTheme, alpha } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const SearchBar = ({ onSearch, searchTerm = '' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (isTyping) {
        onSearch(localSearchTerm);
        setIsTyping(false);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [localSearchTerm, isTyping, onSearch]);

  const handleChange = (e) => {
    setLocalSearchTerm(e.target.value);
    setIsTyping(true);
  };

  const handleSubmit = () => {
    onSearch(localSearchTerm);
    setIsTyping(false);
  };

  const clearSearch = () => {
    setLocalSearchTerm('');
    onSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box
      data-tour="news-search"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: '10px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:focus-within': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
        },
      }}
    >
      <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.disabled, flexShrink: 0 }} />
      <InputBase
        type="text"
        value={localSearchTerm}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={t('news_hub.search.placeholder')}
        fullWidth
        sx={{
          fontSize: '0.875rem',
          color: theme.palette.text.primary,
          '& .MuiInputBase-input::placeholder': {
            color: theme.palette.text.disabled,
            opacity: 1,
          },
        }}
      />
      {localSearchTerm && (
        <IconButton
          onClick={clearSearch}
          size="small"
          title={t('news_hub.search.clear')}
          sx={{
            p: 0.25,
            color: theme.palette.text.disabled,
            '&:hover': { color: theme.palette.text.secondary },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default SearchBar;
